import React, { useState, useEffect } from "react";
import { Header } from "../components/Header";
import axios from "axios";
import { API_ENDPOINTS } from '../config/api';
import { motion } from "framer-motion";
import { Save, Plus, Trash2, MessageSquare, Settings, Link as LinkIcon } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../config/amplitude';

export const SettingsPage = () => {
  const [savedKeywords, setSavedKeywords] = useState<string[]>([]);
  const [newKeyword, setNewKeyword] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{type: 'success' | 'error' | 'info', text: string} | null>(null);
  const [appLink, setAppLink] = useState(localStorage.getItem('user_app_link') || '');
  const [appName, setAppName] = useState(localStorage.getItem('user_app_name') || '');
  const [savedAppLink, setSavedAppLink] = useState(localStorage.getItem('user_app_link') || '');
  const [savedAppName, setSavedAppName] = useState(localStorage.getItem('user_app_name') || '');
  const [commentStats, setCommentStats] = useState({
    totalComments: 0,
    commentedPosts: 0,
    subreddits: 0
  });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem('reddit_token');
      
      if (!token) {
        console.log('No token found, redirecting to login');
        navigate('/');
        return;
      }

      try {
        // Validate token format
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) {
          console.error('Invalid token format');
          handleLogout();
          return;
        }

        const prefsResponse = await axios.get(API_ENDPOINTS.USER_PREFERENCES, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (prefsResponse.data && prefsResponse.data.savedKeywords) {
          // Remove duplicates and filter out default keywords
          const defaultKeywords = ["promote your app", "best ai tools", "best saas tools"];
          const uniqueKeywords = Array.from(new Set<string>(prefsResponse.data.savedKeywords))
            .filter(keyword => !defaultKeywords.includes(keyword));
          setSavedKeywords(uniqueKeywords);
        }
        
        const statsResponse = await axios.get(API_ENDPOINTS.USER_COMMENTS, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (statsResponse.data) {
          setCommentStats({
            totalComments: statsResponse.data.totalComments || 0,
            commentedPosts: statsResponse.data.uniquePosts || 0,
            subreddits: statsResponse.data.uniqueSubreddits || 0
          });
        }
      } catch (err: any) {
        console.error('Error fetching user data:', err);
        
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          handleLogout();
        } else {
          setStatusMessage({type: 'error', text: 'Failed to load settings'});
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  // Add useEffect for auto-dismissing status messages
  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => {
        setStatusMessage(null);
      }, 5000); // 5 seconds

      return () => clearTimeout(timer);
    }
  }, [statusMessage]);

  const handleLogout = () => {
    localStorage.removeItem('reddit_token');
    localStorage.removeItem('reddit_user');
    setStatusMessage({type: 'info', text: 'Session expired. Please log in again.'});
    navigate('/');
  };

  const savePreferences = async () => {
    setIsSaving(true);
    try {
      // Save keywords to server
      await axios.post(
        API_ENDPOINTS.USER_PREFERENCES,
        { savedKeywords },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('reddit_token')}`
          }
        }
      );

      // Save app link to local storage
      localStorage.setItem('user_app_link', appLink);
      
      setStatusMessage({type: 'success', text: 'Settings saved successfully!'});
      trackEvent('Button Clicked', { buttonName: 'Save Settings' });
    } catch (error) {
      console.error("Error saving preferences:", error);
      setStatusMessage({type: 'error', text: 'Failed to save settings'});
    } finally {
      setIsSaving(false);
    }
  };

  // Add a new keyword
  const addKeyword = () => {
    const trimmedKeyword = newKeyword.trim();
    // Check if keyword already exists or is a default keyword
    const defaultKeywords = ["promote your app", "best ai tools", "best saas tools"];
    if (trimmedKeyword && 
        !savedKeywords.includes(trimmedKeyword) && 
        !defaultKeywords.includes(trimmedKeyword)) {
      setSavedKeywords([...savedKeywords, trimmedKeyword]);
      setNewKeyword("");
    } else if (defaultKeywords.includes(trimmedKeyword)) {
      setStatusMessage({type: 'info', text: 'This is a default keyword and cannot be added to saved keywords'});
      setNewKeyword("");
    }
  };

  // Remove a keyword
  const removeKeyword = async (keyword: string) => {
    try {
      // Don't allow removing default keywords (though they shouldn't appear in the list anyway)
      const defaultKeywords = ["promote your app", "best ai tools", "best saas tools"];
      if (defaultKeywords.includes(keyword)) {
        setStatusMessage({type: 'info', text: 'Default keywords cannot be removed'});
        return;
      }

      const updatedKeywords = savedKeywords.filter(k => k !== keyword);
      setSavedKeywords(updatedKeywords);
      
      // Immediately save to server
      await axios.post(
        API_ENDPOINTS.USER_PREFERENCES,
        { savedKeywords: updatedKeywords },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('reddit_token')}`
          }
        }
      );
      
      setStatusMessage({type: 'success', text: 'Keyword removed successfully'});
    } catch (error) {
      console.error("Error removing keyword:", error);
      setStatusMessage({type: 'error', text: 'Failed to remove keyword'});
      // Revert the local state if server save fails
      setSavedKeywords(prev => [...prev, keyword]);
    }
  };

  const saveAppDetails = () => {
    localStorage.setItem('user_app_link', appLink);
    localStorage.setItem('user_app_name', appName);
    setSavedAppLink(appLink);
    setSavedAppName(appName);
    setStatusMessage({type: 'success', text: 'App details saved!'});
    trackEvent('Button Clicked', { buttonName: 'Save Changes' });
  };

  const hasUnsavedAppChanges = appLink !== savedAppLink || appName !== savedAppName;

  const handleEditProduct = () => {
    trackEvent('Button Clicked', { buttonName: 'Edit Product' });
    // ... rest of the function
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-sky-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-gradientStart to-gradientEnd">
      <Header 
        isAuthenticated={!!localStorage.getItem('reddit_token')}
        onLogout={handleLogout}
        onLogin={() => navigate('/')}
      />
      
      {/* Status Message */}
      {statusMessage && (
        <div className={`fixed top-20 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-md shadow-md z-50 ${
          statusMessage.type === 'success' ? 'bg-green-100 text-green-700' :
          statusMessage.type === 'error' ? 'bg-red-100 text-red-700' :
          'bg-blue-100 text-blue-700'
        } transition-opacity duration-300`}>
          {statusMessage.text}
        </div>
      )}

      <div className="max-w-4xl mx-auto py-8 px-4 mt-20">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center mb-6">
            <Settings className="text-sky-500 mr-3" size={24} />
            <h1 className="text-2xl font-bold text-gray-800">Settings</h1>
          </div>

          {/* App Link Section */}
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <LinkIcon className="text-sky-500 mr-2" size={20} />
              <h2 className="text-xl font-semibold text-gray-700">Your Product Details</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Enter your product's name and the URL you want to promote in your comments.
            </p>
            <div className="flex flex-col space-y-4">
              {hasUnsavedAppChanges ? (
                // Edit Mode
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="appName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Product Name
                      </label>
                      <input
                        type="text"
                        id="appName"
                        value={appName}
                        onChange={(e) => setAppName(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white sm:text-sm"
                        placeholder="Enter your product name"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label htmlFor="appLink" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Product Link
                      </label>
                      <input
                        type="url"
                        id="appLink"
                        value={appLink}
                        onChange={(e) => setAppLink(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white sm:text-sm"
                        placeholder="https://your-product-url.com"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div className="space-x-2">
                      <button
                        onClick={() => {
                          setAppName(savedAppName);
                          setAppLink(savedAppLink);
                        }}
                        className="px-4 py-2 rounded-md text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveAppDetails}
                        className="flex items-center space-x-2 px-4 py-2 rounded-md bg-sky-500 text-white hover:bg-sky-600"
                      >
                        <Save size={18} />
                        <span>Save Changes</span>
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                // View Mode
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm font-medium text-gray-500">Product Name</span>
                        <p className="text-lg font-medium text-gray-900">{savedAppName || 'Not set'}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">Product Link</span>
                        <p className="text-gray-900 break-all">
                          {savedAppLink ? (
                            <a href={savedAppLink} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:text-sky-800">
                              {savedAppLink}
                            </a>
                          ) : (
                            'Not set'
                          )}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setAppName(savedAppName);
                        setAppLink(savedAppLink);
                        // Force edit mode by making a small change
                        setAppName(savedAppName + ' ');
                      }}
                      className="flex items-center space-x-1 text-sky-500 hover:text-sky-700"
                    >
                      <span className="text-sm">Edit</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Saved Keywords Section */}
          <div>
            <div className="flex items-center mb-4">
              <MessageSquare className="text-sky-500 mr-2" size={20} />
              <h2 className="text-xl font-semibold text-gray-700">Saved Keywords</h2>
            </div>
            <p className="text-gray-600 mb-4">
              These keywords will be automatically loaded when you open the app.
            </p>
            
            <div className="flex flex-wrap gap-2 mb-4">
              {savedKeywords.map((keyword) => (
                <div 
                  key={keyword}
                  className="flex items-center bg-sky-100 text-sky-700 px-3 py-1 rounded-full"
                >
                  <span>{keyword}</span>
                  <button
                    onClick={() => removeKeyword(keyword)}
                    className="ml-2 text-sky-500 hover:text-sky-700"
                    title="Remove keyword"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              
              {savedKeywords.length === 0 && (
                <p className="text-gray-500 italic">No saved keywords yet.</p>
              )}
            </div>
            
            <div className="flex mb-4">
              <input
                type="text"
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                placeholder="Add a keyword"
                className="flex-grow border border-gray-300 rounded-l-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500"
                onKeyDown={(e) => e.key === "Enter" && addKeyword()}
              />
              <button
                onClick={addKeyword}
                className="bg-sky-500 text-white px-4 py-2 rounded-r-md hover:bg-sky-600 flex items-center"
              >
                <Plus size={18} />
              </button>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end mt-6">
            <button
              onClick={savePreferences}
              disabled={isSaving}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md ${
                isSaving 
                  ? "bg-gray-300 text-gray-500" 
                  : "bg-sky-500 text-white hover:bg-sky-600"
              }`}
            >
              <Save size={18} />
              <span>{isSaving ? "Saving..." : "Save Settings"}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 